import { useId } from "react";
import { motion } from "framer-motion";
import { EASE } from "@/lib/motion";

/**
 * Sonare's product visual: a voice signal held inside a calm conversational
 * field. The open contours suggest exchange rather than a microphone, while
 * the central waveform gives the product a recognisable silhouette.
 */
export default function SonareGraphic({ className = "" }) {
    const uid = useId().replace(/:/g, "");
    const signalId = `${uid}-sonare-signal`;
    const glowId = `${uid}-sonare-glow`;
    const fieldId = `${uid}-sonare-field`;
    const bars = [22, 42, 68, 96, 126, 154, 126, 96, 68, 42, 22];

    return (
        <svg
            viewBox="0 0 600 220"
            className={className}
            aria-hidden="true"
            preserveAspectRatio="xMidYMid meet"
        >
            <defs>
                <linearGradient id={signalId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#6A5CFF" />
                    <stop offset="48%" stopColor="#FF4DA6" />
                    <stop offset="100%" stopColor="#FF8A00" />
                </linearGradient>
                <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#FF4DA6" stopOpacity="0.2" />
                    <stop offset="55%" stopColor="#8A2BE2" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#6A5CFF" stopOpacity="0" />
                </radialGradient>
                <linearGradient id={fieldId} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6A5CFF" stopOpacity="0" />
                    <stop offset="30%" stopColor="#6A5CFF" stopOpacity="0.48" />
                    <stop offset="70%" stopColor="#FF4DA6" stopOpacity="0.48" />
                    <stop offset="100%" stopColor="#FF8A00" stopOpacity="0" />
                </linearGradient>
            </defs>

            <ellipse cx="300" cy="110" rx="172" ry="104" fill={`url(#${glowId})`} />

            {[0, 1, 2].map((i) => (
                <motion.path
                    key={i}
                    d={`M ${76 + i * 22} ${110 - i * 27}
                        C ${158 + i * 6} ${32 + i * 10},
                          ${226 + i * 4} ${33 + i * 14},
                          300 110
                        C ${374 - i * 4} ${187 - i * 14},
                          ${442 - i * 6} ${188 - i * 10},
                          ${524 - i * 22} ${110 + i * 27}`}
                    fill="none"
                    stroke={`url(#${fieldId})`}
                    strokeWidth={i === 0 ? 1.5 : 1}
                    strokeLinecap="round"
                    opacity={0.82 - i * 0.2}
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 0.82 - i * 0.2 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2.2, delay: i * 0.12, ease: EASE }}
                />
            ))}

            {bars.map((height, i) => {
                const x = 220 + i * 16;
                return (
                    <motion.rect
                        key={x}
                        x={x}
                        y={110 - height / 2}
                        width="6"
                        height={height}
                        rx="3"
                        fill={`url(#${signalId})`}
                        initial={{ scaleY: 0, opacity: 0 }}
                        whileInView={{ scaleY: 1, opacity: 0.95 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1, delay: 0.18 + Math.abs(5 - i) * 0.045, ease: EASE }}
                        style={{ transformBox: "fill-box", transformOrigin: "center" }}
                    />
                );
            })}

            <motion.circle
                cx="300"
                cy="110"
                r="91"
                fill="none"
                stroke={`url(#${signalId})`}
                strokeWidth="1"
                strokeDasharray="2 8"
                opacity="0.28"
                initial={{ rotate: -18, opacity: 0 }}
                whileInView={{ rotate: 0, opacity: 0.28 }}
                viewport={{ once: true }}
                transition={{ duration: 2.4, ease: EASE }}
                style={{ transformOrigin: "300px 110px" }}
            />
        </svg>
    );
}
